import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import useFlightSearchParams from "hooks/flight/useFlightSearchParams";
import useGetFlightAvailbility from "hooks/flight/useGetFlightAvailability";
import useApiClient from "hooks/useApiClient";
import {
    getDateRange,
    reformatDateKey,
    makeColor,
    formatDayLabel,
    applyCalendarFilters,
    BEST_PRICE_HEX,
    MID_PRICE_HEX,
    WORST_PRICE_HEX,
    HEADER_BG,
    SELECTION_ACCENT,
} from "helpers/flight-calendar-helpers";

const CELL_CLASS = "flight-calendar-cell";

const cardStyle = {
    backgroundColor: "#fcfcfb",
    borderRadius: 12,
    padding: 20,
    boxShadow: "0 1px 3px rgba(11,11,11,0.10), 0 1px 2px rgba(11,11,11,0.06)",
};

const dateHeaderStyle = {
    border: "1px solid #e1e0d9",
    padding: "10px 8px",
    textAlign: "center",
    whiteSpace: "nowrap",
    backgroundColor: "#f9f9f7",
    color: "#52514e",
    fontWeight: 600,
    fontSize: 13,
};

const bannerStyle = {
    border: "1px solid #e1e0d9",
    padding: "0.9rem",
    textAlign: "center",
    backgroundColor: HEADER_BG,
    color: "#fff",
    fontWeight: 700,
    letterSpacing: 0.3,
};

const emptyCellStyle = {
    border: "1px solid #e1e0d9",
    backgroundColor: "#f9f9f7",
    color: "#c3c2b7",
    textAlign: "center",
    padding: "10px 8px",
};

const FlightCalendarGrid = ({ calendarData, flightSearchParams, filters }) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { updateFlightSearchParams, prepareAvailabilityPayload } = useFlightSearchParams();
    const { getProviders } = useGetFlightAvailbility();
    const { urls } = useApiClient();
    const [selected, setSelected] = useState(null);

    const range = calendarData.range;
    const isRoundTrip = flightSearchParams?.tripType === "rt";
    const outboundDates = useMemo(
        () => getDateRange(flightSearchParams?.departureDate1, range),
        [flightSearchParams, range]
    );
    const inboundDates = useMemo(
        () => (isRoundTrip ? getDateRange(flightSearchParams?.departureDate2, range) : []),
        [flightSearchParams, range, isRoundTrip]
    );

    // The cheapest recommendation still matching the active filters, per date
    // (or date pair) - filtering re-ranks rather than deletes, so a date that
    // has no matching flight just renders as empty (mirrors Angular's
    // flightItem() reading index 0 of the filtered list).
    const getFilteredCell = (outbound, inbound) => {
        const key = reformatDateKey(outbound, inbound);
        const list = calendarData.recommendationsByKey[key];
        if (!list || list.length === 0) return null;
        return applyCalendarFilters(list, filters)[0] ?? null;
    };

    // Recomputed from the currently filtered/visible cells only, same as
    // Angular's calcNewPrices() - so the heatmap always reflects what's on screen.
    const { minPrice, maxPrice } = useMemo(() => {
        const totals = [];
        const pairs = isRoundTrip
            ? inboundDates.flatMap((inbound) => outboundDates.map((outbound) => [outbound, inbound]))
            : outboundDates.map((outbound) => [outbound, undefined]);

        pairs.forEach(([outbound, inbound]) => {
            if (inbound && new Date(inbound) < new Date(outbound)) return;
            const rec = getFilteredCell(outbound, inbound);
            const total = rec?.price?.detailed_price?.total ?? rec?.price?.total;
            if (total != null) totals.push(Number(total));
        });

        if (totals.length === 0) return { minPrice: 0, maxPrice: 0 };
        return { minPrice: Math.min(...totals), maxPrice: Math.max(...totals) };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [calendarData, filters, outboundDates, inboundDates, isRoundTrip]);

    const getCell = (outbound, inbound) => {
        const rec = getFilteredCell(outbound, inbound);
        if (!rec) return null;
        const total = rec?.price?.detailed_price?.total ?? rec?.price?.total;
        if (total == null) return null;
        return {
            total: Number(total),
            currency: rec?.price?.detailed_price?.currency ?? calendarData.currency,
            airline: rec?.validatingCarrier,
        };
    };

    const applySelection = () => {
        if (!selected) return;

        // Keep the search form/context in sync for when the user lands on
        // /flight-search (its inputs read flightSearchParams), and clear
        // "flexible" so a later re-search from that page runs as a normal
        // exact-date search instead of another flexible/range one.
        updateFlightSearchParams("departureDate1", selected.outbound);
        if (isRoundTrip && selected.inbound) {
            updateFlightSearchParams("departureDate2", selected.inbound);
        }
        updateFlightSearchParams("flexible", false);

        // updateFlightSearchParams only schedules a context update, so
        // prepareAvailabilityPayload() called here would still build the payload
        // from the pre-selection dates. Patch the selected dates into the payload
        // directly and pass it straight to getProviders(), so the search runs
        // immediately with the right dates instead of waiting for a re-render.
        // Also force range back to "0": prepareAvailabilityPayload() still sets
        // range="3" here because flightSearchParams.flexible is still true (that's
        // how we got to this page), but the exact-date /availability endpoint only
        // accepts range 0 or 1 - sending 3 makes it come back empty.
        const payload = prepareAvailabilityPayload();
        payload.departureDate_1 = selected.outbound;
        if (isRoundTrip && selected.inbound) {
            payload.departureDate_2 = selected.inbound;
        }
        payload.flexible = false;
        payload.range = "0";

        navigate("/flight-search");
        getProviders(payload);
    };

    const renderCell = (outbound, inbound) => {
        const cell = getCell(outbound, inbound);
        if (!cell) {
            return <td key={inbound ?? outbound} style={emptyCellStyle}>/</td>;
        }

        const id = `flight-choice_${outbound}_${inbound || ""}`;
        const isSelected = selected?.outbound === outbound && selected?.inbound === inbound;
        const priceColor = makeColor(cell.total, minPrice, maxPrice);
        const logoSrc = cell.airline
            ? `${process.env.REACT_APP_STATIC_URL}/${urls.flight.airlines}/${cell.airline}.png`
            : undefined;

        return (
            <td
                key={inbound ?? outbound}
                className={CELL_CLASS}
                onClick={() => setSelected({ outbound, inbound })}
                style={{
                    border: "1px solid #e1e0d9",
                    padding: 0,
                    backgroundColor: "#ffffff",
                    cursor: "pointer",
                    position: "relative",
                    boxShadow: isSelected ? `inset 0 0 0 3px ${SELECTION_ACCENT}` : undefined,
                }}
            >
                <label
                    htmlFor={id}
                    style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4, padding: "10px 8px", cursor: "pointer", margin: 0 }}
                >
                    <input
                        type="radio"
                        name="flight"
                        id={id}
                        checked={isSelected}
                        onChange={() => setSelected({ outbound, inbound })}
                        style={{ position: "absolute", opacity: 0, width: 1, height: 1 }}
                    />
                    {isSelected && (
                        <span
                            style={{
                                position: "absolute",
                                top: 4,
                                [i18n.language === "ar" ? "left" : "right"]: 4,
                                backgroundColor: SELECTION_ACCENT,
                                color: "#ffffff",
                                borderRadius: "50%",
                                width: 16,
                                height: 16,
                                fontSize: 11,
                                lineHeight: "16px",
                                textAlign: "center",
                                fontWeight: 700,
                            }}
                        >
                            ✓
                        </span>
                    )}
                    <span style={{ color: priceColor, fontWeight: 700, whiteSpace: "nowrap" }}>
                        {cell.total} {cell.currency || ""}
                    </span>
                    {logoSrc && (
                        <span
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 26,
                                height: 26,
                                borderRadius: "50%",
                                backgroundColor: "#ffffff",
                                padding: 2,
                            }}
                        >
                            <img src={logoSrc} alt="" style={{ width: 20, height: 20, objectFit: "contain" }} />
                        </span>
                    )}
                </label>
            </td>
        );
    };

    return (
        <>
            <style>{`.${CELL_CLASS}:hover { filter: brightness(0.93); }`}</style>
            <div style={cardStyle}>
                <div style={{ overflowX: "auto" }}>
                    {!isRoundTrip ? (
                        <table style={{ borderCollapse: "collapse", minWidth: 500, width: "100%" }}>
                            <thead>
                                <tr>
                                    <th style={{ ...bannerStyle, borderRadius: "8px 8px 0 0" }} colSpan={outboundDates.length}>
                                        Départ
                                    </th>
                                </tr>
                                <tr>
                                    {outboundDates.map((d) => (
                                        <th key={d} style={dateHeaderStyle}>{formatDayLabel(d, i18n.language)}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                <tr>{outboundDates.map((outbound) => renderCell(outbound))}</tr>
                            </tbody>
                        </table>
                    ) : (
                        <table style={{ borderCollapse: "collapse", minWidth: 700, width: "100%" }}>
                            <thead>
                                <tr>
                                    <th colSpan={2} style={{ border: "none" }}></th>
                                    <th style={bannerStyle} colSpan={outboundDates.length}>Départ</th>
                                </tr>
                                <tr>
                                    <th colSpan={2} style={{ border: "none" }}></th>
                                    {outboundDates.map((d) => (
                                        <th key={d} style={dateHeaderStyle}>{formatDayLabel(d, i18n.language)}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {inboundDates.map((inbound, inboundIndex) => (
                                    <tr key={inbound}>
                                        {inboundIndex === 0 && (
                                            <th rowSpan={inboundDates.length} style={bannerStyle}>
                                                <div style={{ transform: "rotate(-90deg)" }}>Retour</div>
                                            </th>
                                        )}
                                        <th style={dateHeaderStyle}>{formatDayLabel(inbound, i18n.language)}</th>
                                        {outboundDates.map((outbound) =>
                                            new Date(inbound) < new Date(outbound) ? (
                                                <td key={outbound} style={emptyCellStyle}></td>
                                            ) : (
                                                renderCell(outbound, inbound)
                                            )
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 20, fontSize: 13, color: "#52514e" }}>
                    <span>Moins cher</span>
                    <div style={{ flex: 1, height: 8, borderRadius: 4, background: `linear-gradient(90deg, ${BEST_PRICE_HEX}, ${MID_PRICE_HEX}, ${WORST_PRICE_HEX})` }} />
                    <span>Plus cher</span>
                </div>

                <div className="text-center mt-4">
                    <button className="btn btn_theme btn_md" disabled={!selected} onClick={applySelection}>
                        {t("seeFlights")}
                    </button>
                </div>
            </div>
        </>
    );
};

export default FlightCalendarGrid;
